import { supabase } from '../lib/supabase';
import type { ProviderUnavailableError, MonthlyLimitReachedError } from '../types/insights.types';
import type { ChatMessageMetadata, ChatMessageRow } from '../types/ai-chat.types';

export type { ChatMessageMetadata, ChatMessageRow };

export interface ChatHistoryStatus {
  messageCount: number;
  oldestMessageDays: number | null;
  lastClearedAt: string | null;
}

export class AiChatHistoryService {
  async clearHistory(userId: string): Promise<void> {
    try {
      // Delete all chat messages for the user.
      await (supabase as any).from('ai_chat_messages').delete().eq('user_id', userId);

      // Track last manually cleared timestamp on user_ai_settings if possible.
      await (supabase as any)
        .from('user_ai_settings')
        .update({ chat_history_last_cleared_at: new Date().toISOString() })
        .eq('user_id', userId);
    } catch (error) {
      // In test mode or when Supabase is not configured, fail silently.
      console.warn('[AiChatHistoryService.clearHistory] Non-fatal error:', error);
    }
  }

  /** Get chat messages for user, ordered by created_at (contract: id, role, content, created_at, metadata?). */
  async getHistory(userId: string): Promise<ChatMessageRow[]> {
    const rows = await this.exportHistory(userId);
    return (rows ?? []).map((r: any) => ({
      id: r.id,
      role: r.role,
      content: r.content,
      created_at: r.created_at,
      ...(r.metadata != null ? { metadata: r.metadata as ChatMessageMetadata } : {}),
    }));
  }

  async exportHistory(userId: string): Promise<any[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('ai_chat_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) {
        console.warn('[AiChatHistoryService.exportHistory] Error fetching history:', error);
        return [];
      }

      return data ?? [];
    } catch (error) {
      console.warn('[AiChatHistoryService.exportHistory] Non-fatal error:', error);
      return [];
    }
  }

  async getStatus(userId: string): Promise<ChatHistoryStatus> {
    try {
      const { data, error, count } = await (supabase as any)
        .from('ai_chat_messages')
        .select('created_at', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) {
        console.warn('[AiChatHistoryService.getStatus] Error fetching history status:', error);
        return { messageCount: 0, oldestMessageDays: null, lastClearedAt: null };
      }

      const now = new Date();
      let oldestMessageDays: number | null = null;
      if (data && data.length > 0 && data[0].created_at) {
        const oldest = new Date(data[0].created_at as string);
        const diffMs = now.getTime() - oldest.getTime();
        oldestMessageDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      }

      const { data: settingsRow } = await (supabase as any)
        .from('user_ai_settings')
        .select('chat_history_last_cleared_at')
        .eq('user_id', userId)
        .single();

      return {
        messageCount: count ?? 0,
        oldestMessageDays,
        lastClearedAt: settingsRow?.chat_history_last_cleared_at ?? null,
      };
    } catch (error) {
      console.warn('[AiChatHistoryService.getStatus] Non-fatal error:', error);
      return { messageCount: 0, oldestMessageDays: null, lastClearedAt: null };
    }
  }
}

export interface SendMessageResult {
  assistant_message?: {
    id: string | null;
    role: string;
    content: string;
    created_at: string;
    metadata?: ChatMessageMetadata | null;
  };
}

type EdgeFunctionPayload = {
  error?: string;
  message?: string;
  retry_after?: number;
  provider_error_type?: string | null;
  assistant_message?: SendMessageResult['assistant_message'];
};

/** Parse JSON body from supabase.functions.invoke when status is non-2xx. */
async function readEdgeFunctionPayload(
  data: unknown,
  error: unknown
): Promise<EdgeFunctionPayload | null> {
  if (data && typeof data === 'object') {
    return data as EdgeFunctionPayload;
  }

  if (!error || typeof error !== 'object') {
    return null;
  }

  try {
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      const bodyJson = await ctx.json();
      if (bodyJson && typeof bodyJson === 'object') {
        return bodyJson as EdgeFunctionPayload;
      }
    }
  } catch {
    // ignore body parse errors
  }

  return null;
}

function mapEdgeFunctionPayload(
  payload: EdgeFunctionPayload | null,
  fallbackMessage: string
): SendMessageResult | ProviderUnavailableError | MonthlyLimitReachedError {
  if (payload?.error === 'provider_unavailable') {
    return {
      error: 'provider_unavailable',
      message: payload.message ?? 'The AI service is temporarily unavailable. Please try again.',
      retry_after: payload.retry_after,
    };
  }

  if (payload?.error === 'monthly_limit_reached') {
    return {
      error: 'monthly_limit_reached',
      message:
        payload.message ??
        'Your monthly AI spend limit has been reached. Adjust it in Settings → AI Configuration, or try again next month.',
    };
  }

  if (payload?.assistant_message) {
    return { assistant_message: payload.assistant_message };
  }

  const detail =
    (typeof payload?.message === 'string' && payload.message) ||
    (typeof payload?.error === 'string' && payload.error) ||
    fallbackMessage;

  if (
    payload?.error === 'provider_unavailable' ||
    detail.toLowerCase().includes('unavailable') ||
    detail.toLowerCase().includes('timeout') ||
    detail.toLowerCase().includes('rate limit')
  ) {
    return {
      error: 'provider_unavailable',
      message: detail,
      retry_after: payload?.retry_after ?? 60,
    };
  }

  throw new Error(detail);
}

/**
 * Send a message to Insights Chat (Edge Function ai-insights-chat).
 * Returns assistant_message on success, or throws/returns ProviderUnavailableError on 503.
 */
export async function sendMessage(
  _userId: string,
  content: string
): Promise<SendMessageResult | ProviderUnavailableError | MonthlyLimitReachedError> {
  const { data, error } = await (supabase as any).functions.invoke('ai-insights-chat', {
    body: { content: content.trim() },
  });

  const payload = await readEdgeFunctionPayload(data, error);
  const fallbackMessage = error instanceof Error ? error.message : String(error ?? 'Failed to get response from Insights Chat');

  if (error) {
    return mapEdgeFunctionPayload(payload, fallbackMessage);
  }

  return mapEdgeFunctionPayload(payload, fallbackMessage);
}
